import type { Editor, Range } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export type TaskDeletionInfo = {
    isLastTask: boolean;
};

/**
 * Gets the document range required to remove a task item and its nested sub-tasks.
 *
 * Accepts a raw document so callers can re-derive the range against an in-progress
 * transaction (`transaction.doc`) before dispatching, not just the editor's current state.
 */
export const getTaskItemDeletionRange = (doc: ProseMirrorNode, position: number): Range | null => {
    const taskItem = doc.nodeAt(position);

    if (!taskItem) return null;

    const $position = doc.resolve(position);
    const isOnlyChildOfList = $position.parent.childCount === 1;
    const isLastTask = $position.depth === 1 && isOnlyChildOfList;

    if (isLastTask) return null;

    // Deleting the only child of a nested list would leave an empty taskList, which the
    // schema forbids — remove the wrapping list instead.
    const from = isOnlyChildOfList ? $position.before() : position;
    const to = isOnlyChildOfList ? $position.after() : position + taskItem.nodeSize;

    return { from, to };
};

/**
 * Deletes the task item at the given position, along with any nested sub-tasks.
 *
 * If the task is the last remaining top-level task, the document is left unchanged and the
 * callback is invoked with `isLastTask: true` so the consumer can decide how to handle it.
 */
export const deleteTaskItem = (
    editor: Editor,
    position: number,
    onDeleteTask?: (info: TaskDeletionInfo) => void,
): void => {
    if (!editor.state.doc.nodeAt(position)) return;

    const deletionRange = getTaskItemDeletionRange(editor.state.doc, position);

    if (!deletionRange) {
        onDeleteTask?.({ isLastTask: true });
        return;
    }

    editor.commands.deleteRange(deletionRange);
    onDeleteTask?.({ isLastTask: false });
};
